/**
 * Пустое состояние, состояние загрузки и ошибка запроса.
 * Классы взяты из макета: `.list-empty` (блок по центру) и `.detail-empty` (строка внутри блока).
 */

import type { ReactNode } from 'react';

import { isApiError } from '../api/client';

const CODE_MESSAGES: Record<string, string> = {
    NETWORK: 'Нет связи с сервером. Проверьте подключение и повторите.',
    UNAUTHORIZED: 'Сессия истекла. Войдите в систему заново.',
    FORBIDDEN: 'Недостаточно прав для этого действия.',
    NOT_FOUND: 'Объект не найден или относится к другой клинике.',
    CONFLICT: 'Действие невозможно в текущем состоянии дела.',
    PAYLOAD_TOO_LARGE: 'Файл слишком большой.',
    UNSUPPORTED_MEDIA: 'Тип файла не поддерживается.',
    VALIDATION: 'Проверьте заполнение полей.',
    UPSTREAM_ERROR: 'Сервис разбора обращений ответил ошибкой. Попробуйте повторить.',
    UPSTREAM_TIMEOUT: 'Сервис разбора обращений не ответил вовремя. Попробуйте повторить.',
    INTERNAL: 'Внутренняя ошибка сервера.',
};

/** Человеческий текст ошибки: сообщение бэкенда важнее подстановки по коду. */
export function errorMessage(error: unknown): string {
    if (!error) return '';
    if (isApiError(error)) {
        if (error.message) return error.message;
        return CODE_MESSAGES[String(error.code)] ?? 'Не удалось выполнить запрос.';
    }
    if (error instanceof Error && error.message) return error.message;
    return 'Не удалось выполнить запрос.';
}

/**
 * Ошибка запроса, у которого ещё нет данных: после первой неудачи — пока повторы идут в фоне (`failureReason`) — и
 * после последней (`error`). Данные пришли — ошибки нет (React Query сбрасывает счётчик неудач при успехе).
 */
export function pendingFailure(query: { data?: unknown; error: unknown; failureReason: unknown; failureCount: number }): unknown {
    if (query.data !== undefined) return null;
    if (query.error) return query.error;
    return query.failureCount > 0 ? (query.failureReason ?? new Error('Не удалось загрузить данные')) : null;
}

/** Код ошибки для ветвлений в экранах (`FORBIDDEN`, `CONFLICT`, …). */
export function errorCode(error: unknown): string | null {
    return isApiError(error) ? String(error.code) : null;
}

export interface ErrorStateProps {
    error?: unknown;
    title?: string;
    message?: ReactNode;
    onRetry?: () => void;
    retryLabel?: string;
    children?: ReactNode;
    /** `inline` — строка внутри блока (`.detail-empty`), по умолчанию блок по центру. */
    variant?: 'block' | 'inline';
}

export function ErrorState({
    error,
    title = 'Не удалось загрузить данные',
    message,
    onRetry,
    retryLabel = 'Повторить',
    children,
    variant = 'block',
}: ErrorStateProps) {
    const text = message ?? errorMessage(error);
    if (variant === 'inline') {
        return <div className="detail-empty">{text}</div>;
    }
    return (
        <div className="list-empty" role="alert">
            <div className="state-title">{title}</div>
            <div>{text}</div>
            {(onRetry || children) && (
                <div className="state-actions">
                    {onRetry && (
                        <button type="button" className="act-btn" onClick={onRetry}>
                            {retryLabel}
                        </button>
                    )}
                    {children}
                </div>
            )}
        </div>
    );
}

export interface EmptyStateProps {
    /** Текст макета для пустой выборки — «В выборке нет дел.» */
    children?: ReactNode;
    variant?: 'block' | 'inline';
}

export function EmptyState({ children = 'В выборке нет дел.', variant = 'block' }: EmptyStateProps) {
    if (variant === 'inline') return <div className="detail-empty">{children}</div>;
    return <div className="list-empty">{children}</div>;
}

export interface LoadingStateProps {
    label?: string;
    variant?: 'block' | 'inline';
}

export function LoadingState({ label = 'Загрузка…', variant = 'block' }: LoadingStateProps) {
    return (
        <div className={variant === 'inline' ? 'detail-empty loading-line' : 'list-empty loading-line'} role="status" aria-live="polite">
            <span className="spinner" aria-hidden="true" />
            {label}
        </div>
    );
}

/** Скелетон строк таблицы на время первой загрузки: место под строки видно сразу, страница не прыгает. */
export function SkeletonRows({ rows = 6, label = 'Загрузка…' }: { rows?: number; label?: string }) {
    return (
        <div className="skeleton-rows" role="status" aria-live="polite" aria-label={label}>
            {Array.from({ length: rows }, (_, index) => (
                <div className="skeleton-row" key={index}>
                    <span className="skeleton-bar w-sm" />
                    <span className="skeleton-bar w-lg" />
                    <span className="skeleton-bar w-md" />
                    <span className="skeleton-bar w-sm" />
                </div>
            ))}
        </div>
    );
}

/** Красная строка под формой — тот же элемент, что `.period-error` в панели периода. */
export function FormError({ children }: { children?: ReactNode }) {
    if (!children) return null;
    return (
        <div className="period-error show" role="alert">
            {children}
        </div>
    );
}
